/**
 * «Анкета» → WYSIWYG-досье. Главная канва совпадает с клиентской карточкой:
 * оператор тапает по портрету, имени, блогу, медиа или новостям и правит их
 * на месте через кастомные sheets. URL для фото не показываем.
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  type ActorProfileFields,
  type CropRect,
  type CropUsage,
  type MediaAsset,
  type NewsInput,
  type NewsPost,
  type NewsResult,
  parseArchiveNotes,
} from "../../hooks/useOperatorProfile";
import { CropEditor, cropPreviewStyle, defaultCrop } from "./CropEditor";
import { FILTER_PRESETS, currentPreset, filterToCss } from "./filters";
import { ACrop, AEye, AFan, AMedia, APlay, APlus, AProfile, ATrash, AUpload, AWand, RClose } from "./icons";
import { api } from "../../api/client";
import type { Quest } from "../../api/types";
import css from "./anketa.module.css";

const MAX_BYTES = 20 * 1024 * 1024;
const DOSSIER_ASPECT = 3 / 4;
const TILE_ASPECT = 1;

type Mode = "editor" | "preview";
type EditTarget = "identity" | "portrait" | "blog" | "media" | "news" | "archive" | "temple" | null;

export interface ProfileTabHandle {
  collect: () => ActorProfileFields;
  dirty: boolean;
}

export function ProfileTab({
  initial,
  media,
  news,
  onDirtyChange,
  onAutoSave,
  registerCollect,
  onUpload,
  onSaveCrops,
  onSaveFilter,
  onSetFanOnly,
  onSetFanPortrait,
  onDelete,
  onSaveNews,
  onToast,
  view,
  restoreDraft,
}: {
  initial: ActorProfileFields;
  media: MediaAsset[];
  news: NewsPost[];
  onDirtyChange: (dirty: boolean) => void;
  /** Фоновое автосохранение полей (дебаунс). Возвращает true при успехе. */
  onAutoSave: (fields: ActorProfileFields) => Promise<boolean>;
  registerCollect: (fn: () => ActorProfileFields) => void;
  onUpload: (file: File, opts?: { label?: string; width?: number; height?: number }) => Promise<{ ok: boolean; mediaId?: number; error?: string }>;
  onSaveCrops: (mediaId: number, crops: Partial<Record<CropUsage, CropRect>>) => Promise<{ ok: boolean; error?: string }>;
  onSaveFilter: (mediaId: number, filter: Record<string, unknown>) => Promise<{ ok: boolean; error?: string }>;
  onSetFanOnly: (mediaId: number, fanOnly: boolean) => Promise<{ ok: boolean; error?: string }>;
  onSetFanPortrait: (mediaId: number, fanPortrait: boolean) => Promise<{ ok: boolean; error?: string }>;
  onDelete: (mediaId: number) => Promise<{ ok: boolean; error?: string }>;
  onSaveNews: (input: NewsInput, id?: number) => Promise<NewsResult>;
  onToast: (msg: string, isErr?: boolean) => void;
  view: "normal" | "fan" | "preview";
  restoreDraft: ActorProfileFields | null;
}) {
  const portraitFileRef = useRef<HTMLInputElement>(null);
  const fanFileRef = useRef<HTMLInputElement>(null);
  const mediaFileRef = useRef<HTMLInputElement>(null);
  const circleFileRef = useRef<HTMLInputElement>(null);
  const cropTimers = useRef<{ portrait?: number; media?: number }>({});
  // Квесты для пикера спецверсии тянем напрямую (у роли operator есть perm quests),
  // НЕ через useAppData: его Promise.all включает mc/tea-rooms, которых у оператора нет,
  // и весь батч реджектится → список оказывался пустым.
  const [quests, setQuests] = useState<Quest[]>([]);
  useEffect(() => {
    let alive = true;
    api<{ results?: Quest[] }>("/api/quests")
      .then((r) => { if (alive) setQuests(r.results ?? []); })
      .catch(() => { if (alive) setQuests([]); });
    return () => { alive = false; };
  }, []);
  const [f, setF] = useState<ActorProfileFields>(initial);
  const [bullets, setBullets] = useState<string[]>(() => parseArchiveNotes(initial.archive_notes));
  const [portraitId, setPortraitId] = useState<number | null>(() => resolvePortrait(initial.photo_url, media)?.id ?? null);
  const [selectedMediaId, setSelectedMediaId] = useState<number | null>(null);
  const [busyUpload, setBusyUpload] = useState<"portrait" | "media" | "fan" | null>(null);
  const [subscriberView, setSubscriberView] = useState(false);
  const [edit, setEdit] = useState<EditTarget>(null);
  const lastRestoredDraft = useRef<string | null>(null);
  // Снимок последнего сохранённого состояния — чтобы автосейв не зацикливался и не повторял один и тот же сейв.
  const savedRef = useRef<string>(serializeFields(initial, parseArchiveNotes(initial.archive_notes)));
  // Анти-потеря: pending = последний несохранённый снимок; flush дослаёт его при уходе.
  // savingRef сериализует сейвы (один в полёте) → старый не перезатрёт новый.
  const pendingRef = useRef<{ cur: string; fields: ActorProfileFields } | null>(null);
  const savingRef = useRef(false);
  const cropPending = useRef<{ portrait?: () => void; media?: () => void }>({});
  const flushRef = useRef<() => void>(() => {});
  useEffect(() => {
    flushRef.current = () => {
      const p = pendingRef.current;
      if (!p || savingRef.current) return;
      pendingRef.current = null;
      savedRef.current = p.cur;
      savingRef.current = true;
      void onAutoSave(p.fields).then((ok) => {
        savingRef.current = false;
        if (ok) onDirtyChange(false);
        else savedRef.current = "";
        if (pendingRef.current) flushRef.current(); // догоняем правки, пришедшие во время сейва
      });
    };
  });

  // media обновляется после загрузки фото — синхронизируем выбор портрета/плитки,
  // но НЕ трогаем f/bullets: их владелец — автосейв, и reload не должен затирать ввод.
  useEffect(() => {
    setPortraitId((cur) => cur ?? resolvePortrait(initial.photo_url, media)?.id ?? null);
    setSelectedMediaId((id) => (id && media.some((m) => m.id === id) ? id : media[0]?.id ?? null));
  }, [initial.photo_url, media]);

  useEffect(() => {
    if (!restoreDraft) return;
    const key = JSON.stringify(restoreDraft);
    if (lastRestoredDraft.current === key) return;
    lastRestoredDraft.current = key;
    setF(restoreDraft);
    setBullets(parseArchiveNotes(restoreDraft.archive_notes));
    setPortraitId(resolvePortrait(restoreDraft.photo_url, media)?.id ?? null);
  }, [restoreDraft, media]);

  const portrait = useMemo(() => {
    if (portraitId != null) return media.find((m) => m.id === portraitId);
    return resolvePortrait(f.photo_url, media);
  }, [f.photo_url, media, portraitId]);
  const portraitUrl = portraitSrc(portrait) || f.photo_url;
  const photos = useMemo(() => media.filter((m) => m.kind === "photo"), [media]);
  const selectedMedia = useMemo(
    () => media.find((m) => m.id === selectedMediaId) ?? media[0],
    [media, selectedMediaId],
  );
  const completion = useMemo(
    () => profileCompletion(f, bullets, portraitUrl, media, news),
    [f, bullets, portraitUrl, media, news],
  );

  useEffect(() => {
    const src = portraitSrc(portrait);
    if (src && f.photo_url !== src) setF((prev) => ({ ...prev, photo_url: src }));
  }, [portrait, f.photo_url]);

  useEffect(() => {
    registerCollect(() => ({ ...f, archive_notes: JSON.stringify(bullets.filter((b) => b.trim())) }));
  }, [f, bullets, registerCollect]);

  // Автосохранение: дебаунс 800мс. Снимок кладём в pending и дослаём через flush —
  // тогда последний ввод не теряется при размонтировании/закрытии (эффект ниже).
  useEffect(() => {
    const fields: ActorProfileFields = { ...f, archive_notes: JSON.stringify(bullets.filter((b) => b.trim())) };
    const cur = JSON.stringify(fields);
    const dirty = cur !== savedRef.current;
    onDirtyChange(dirty);
    if (!dirty) { pendingRef.current = null; return; }
    pendingRef.current = { cur, fields };
    const t = window.setTimeout(() => flushRef.current(), 800);
    return () => window.clearTimeout(t);
  }, [f, bullets, onDirtyChange]);

  // Дослать pending при уходе: смена под-вкладки (размонтирование), скрытие/закрытие мини-аппа.
  useEffect(() => {
    const onHide = () => { if (document.visibilityState === "hidden") flushRef.current(); };
    const onPageHide = () => flushRef.current();
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("pagehide", onPageHide);
    return () => {
      document.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("pagehide", onPageHide);
      flushRef.current();
    };
  }, []);

  useEffect(() => () => {
    // на размонтировании дослать незавершённый кроп, а не молча отменить
    if (cropTimers.current.portrait) window.clearTimeout(cropTimers.current.portrait);
    if (cropTimers.current.media) window.clearTimeout(cropTimers.current.media);
    cropPending.current.portrait?.();
    cropPending.current.media?.();
  }, []);

  const set = <K extends keyof ActorProfileFields>(k: K, v: ActorProfileFields[K]) =>
    setF((p) => ({ ...p, [k]: v }));

  async function uploadPortrait(files: FileList | null) {
    const file = files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      onToast("Портретом может быть только фото", true);
      return;
    }
    if (file.size > MAX_BYTES) {
      onToast(`Файл больше 20 МБ (${(file.size / 1048576).toFixed(1)} МБ)`, true);
      return;
    }
    setBusyUpload("portrait");
    const dims = await readDims(file).catch(() => null);
    const res = await onUpload(file, { label: `Портрет · ${file.name}`, width: dims?.w, height: dims?.h });
    setBusyUpload(null);
    if (portraitFileRef.current) portraitFileRef.current.value = "";
    if (res.ok) {
      if (res.mediaId) setPortraitId(res.mediaId);
      onToast("Портрет загружен — уложи кадр в досье");
      setEdit("portrait");
    } else {
      onToast(res.error ?? "Загрузка не удалась", true);
    }
  }

  // Фан-аватар: грузим как обычное фото и сразу помечаем его фан-аватаром — без захода в «Медиа».
  async function uploadFanPortrait(files: FileList | null) {
    const file = files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      onToast("Фан-аватаром может быть только фото", true);
      return;
    }
    if (file.size > MAX_BYTES) {
      onToast(`Файл больше 20 МБ (${(file.size / 1048576).toFixed(1)} МБ)`, true);
      return;
    }
    setBusyUpload("fan");
    const dims = await readDims(file).catch(() => null);
    const res = await onUpload(file, { label: `Фан-аватар · ${file.name}`, width: dims?.w, height: dims?.h });
    if (res.ok && res.mediaId) await onSetFanPortrait(res.mediaId, true);
    setBusyUpload(null);
    if (fanFileRef.current) fanFileRef.current.value = "";
    onToast(res.ok ? "Фан-аватар загружен" : (res.error ?? "Загрузка не удалась"), !res.ok);
  }

  async function uploadCircle(files: FileList | null) {
    const file = files?.[0];
    if (!file) return;
    if (!file.type.startsWith("video/")) { onToast("Кружок — это видео, запиши себя в образе", true); return; }
    if (file.size > MAX_BYTES) { onToast(`Файл больше 20 МБ (${(file.size / 1048576).toFixed(1)} МБ)`, true); return; }
    setBusyUpload("media");
    const res = await onUpload(file, { label: `Кружок · ${file.name.replace(/\.[^.]+$/, "")}` });
    if (circleFileRef.current) circleFileRef.current.value = "";
    if (res.ok && res.mediaId) {
      await onSetFanOnly(res.mediaId, true);
      setSelectedMediaId(res.mediaId);
      onToast("Кружок добавлен");
    } else if (!res.ok) {
      onToast(res.error || "Не удалось загрузить кружок", true);
    }
    setBusyUpload(null);
  }

  async function uploadMedia(files: FileList | null) {
    const file = files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/") && !file.type.startsWith("video/")) {
      onToast("Можно загрузить фото или видео", true);
      return;
    }
    if (file.size > MAX_BYTES) {
      onToast(`Файл больше 20 МБ (${(file.size / 1048576).toFixed(1)} МБ)`, true);
      return;
    }
    setBusyUpload("media");
    const dims = file.type.startsWith("image/") ? await readDims(file).catch(() => null) : null;
    const res = await onUpload(file, { label: file.name.replace(/\.[^.]+$/, ""), width: dims?.w, height: dims?.h });
    setBusyUpload(null);
    if (mediaFileRef.current) mediaFileRef.current.value = "";
    if (res.ok) {
      if (res.mediaId) setSelectedMediaId(res.mediaId);
      onToast("Медиа добавлено в архив");
      setEdit("media");
    } else {
      onToast(res.error ?? "Загрузка не удалась", true);
    }
  }

  async function removeMedia(asset: MediaAsset) {
    const res = await onDelete(asset.id);
    if (res.ok) {
      setSelectedMediaId((id) => (id === asset.id ? null : id));
      onToast("Медиа удалено");
    } else {
      onToast(res.error ?? "Не удалось удалить", true);
    }
  }

  function selectPortrait(asset: MediaAsset) {
    setPortraitId(asset.id);
    const src = portraitSrc(asset);
    if (src) set("photo_url", src);
  }

  function savePortraitCrop(next: CropRect) {
    if (!portrait) return;
    const merged = { ...portrait.crops, dossier_hero: next };
    const doSave = () => { cropPending.current.portrait = undefined; void onSaveCrops(portrait.id, merged); };
    cropPending.current.portrait = doSave;
    if (cropTimers.current.portrait) window.clearTimeout(cropTimers.current.portrait);
    cropTimers.current.portrait = window.setTimeout(doSave, 340);
  }

  function saveMediaCrop(asset: MediaAsset, next: CropRect) {
    const merged = { ...asset.crops, media_tile: next };
    const doSave = () => { cropPending.current.media = undefined; void onSaveCrops(asset.id, merged); };
    cropPending.current.media = doSave;
    if (cropTimers.current.media) window.clearTimeout(cropTimers.current.media);
    cropTimers.current.media = window.setTimeout(doSave, 340);
  }

  async function pickFilter(asset: MediaAsset | undefined, preset: string) {
    if (!asset) return;
    const res = await onSaveFilter(asset.id, { ...asset.filter, preset });
    if (!res.ok) onToast(res.error ?? "Не удалось сохранить фильтр", true);
  }

  async function toggleFan(asset: MediaAsset) {
    const res = await onSetFanOnly(asset.id, !asset.fanOnly);
    if (!res.ok) onToast(res.error ?? "Не удалось сменить доступ", true);
  }

  return (
    <div className={css.wysiwyg}>
      <div className={css.wysiwygToolbar}>
        <div className={css.wysiwygProgress}>
          <span className={css.wysiwygProgressLabel}>герой собран</span>
          <span className={css.wysiwygProgressTrack}><i style={{ width: `${completion.percent}%` }} /></span>
          <strong>{completion.percent}%</strong>
          <em>{completion.next}</em>
        </div>
      </div>

      {view === "preview" ? (
        <>
          <div className={css.wysiwygHint}>Так карточку видит клиент. Переключи аудиторию ниже.</div>
          <div className={css.audienceToggle} role="group" aria-label="Как клиент видит карточку">
            <span>Показать:</span>
            <button type="button" className={!subscriberView ? css.on : ""} onClick={() => setSubscriberView(false)}>без подписки</button>
            <button type="button" className={subscriberView ? css.on : ""} onClick={() => setSubscriberView(true)}>как подписчик</button>
          </div>
          <ClientActorPreview profile={f} bullets={bullets} portrait={portrait} portraitUrl={portraitUrl} media={media} news={news} joined={subscriberView} />
        </>
      ) : (
        <>
          <div className={css.scopeHeader}>{view === "fan" ? "Фан-страница — как видит подписчик" : "Обычная страница — как видит клиент"}</div>
          <div className={css.wysiwygHint}>Тапни блок, чтобы отредактировать — раскладка ровно как у клиента.</div>
          <DossierCanvas
            mode="editor"
            joined={view === "fan"}
            profile={f}
            bullets={bullets}
            portrait={portrait}
            portraitUrl={portraitUrl}
            media={media}
            news={news}
            completion={completion}
            onEdit={setEdit}
            onMediaEdit={(asset) => {
              if (asset) setSelectedMediaId(asset.id);
              setEdit("media");
            }}
            onRecordCircle={() => circleFileRef.current?.click()}
          />
        </>
      )}

      <input ref={portraitFileRef} className={css.hiddenInput} type="file" accept="image/*" onChange={(e) => uploadPortrait(e.target.files)} />
      <input ref={fanFileRef} className={css.hiddenInput} type="file" accept="image/*" onChange={(e) => uploadFanPortrait(e.target.files)} />
      <input ref={mediaFileRef} className={css.hiddenInput} type="file" accept="image/*,video/*" onChange={(e) => uploadMedia(e.target.files)} />
      <input ref={circleFileRef} className={css.hiddenInput} type="file" accept="video/*" capture="user" onChange={(e) => uploadCircle(e.target.files)} />

      {edit === "identity" ? (
        <EditSheet title="Это меняет блок “Имя” в карточке клиента" preview={<IdentityMini profile={f} /> } onClose={() => setEdit(null)}>
          <label className={css.field}>
            <span className={css.fieldLbl}>Имя персонажа</span>
            <input className={css.input} value={f.character_name} maxLength={120}
              placeholder="Имя в клиентском досье"
              onChange={(e) => set("character_name", e.target.value)} />
          </label>
          <label className={css.field}>
            <span className={css.fieldLbl}>Роль</span>
            <input className={css.input} value={f.display_role} maxLength={80}
              placeholder="Проводник, хранитель, пациент, культовый контакт"
              onChange={(e) => set("display_role", e.target.value)} />
          </label>
          <label className={css.field}>
            <span className={css.fieldLbl}>Статус персонажа</span>
            <input className={css.input} value={f.character_status} maxLength={80}
              placeholder="Контакт подтверждён / доступ ограничен"
              onChange={(e) => set("character_status", e.target.value)} />
          </label>
        </EditSheet>
      ) : null}

      {edit === "blog" ? (
        <EditSheet title="Это меняет блок “Описание” в карточке клиента" preview={<LegendMini text={f.atmos_status} />} onClose={() => setEdit(null)}>
          <label className={css.field}>
            <span className={css.fieldLbl}>Описание / блог</span>
            <textarea className={css.textArea} value={f.atmos_status} maxLength={2000} rows={9}
              placeholder="Пиши как выдержку из закрытого дела: манера, секрет, особые моменты с игроками."
              onChange={(e) => set("atmos_status", e.target.value)} />
          </label>
          <div className={css.sheetHint}>{f.atmos_status.length}/2000 · этот текст виден клиентам в hero-блоке.</div>
        </EditSheet>
      ) : null}

      {edit === "portrait" ? (
        <PortraitSheet
          photos={photos}
          portrait={portrait}
          portraitUrl={portraitUrl}
          busy={busyUpload === "portrait"}
          onUpload={() => portraitFileRef.current?.click()}
          onPick={selectPortrait}
          onCrop={savePortraitCrop}
          onFilter={(preset) => pickFilter(portrait, preset)}
          onSetFanPortrait={onSetFanPortrait}
          onUploadFan={() => fanFileRef.current?.click()}
          busyFan={busyUpload === "fan"}
          onClose={() => setEdit(null)}
        />
      ) : null}

      {edit === "media" ? (
        <MediaSheet
          media={media}
          selected={selectedMedia}
          busy={busyUpload === "media"}
          onSelect={(asset) => setSelectedMediaId(asset.id)}
          onUpload={() => mediaFileRef.current?.click()}
          onCrop={saveMediaCrop}
          onFilter={(asset, preset) => pickFilter(asset, preset)}
          onToggleFan={toggleFan}
          onDelete={removeMedia}
          onClose={() => setEdit(null)}
        />
      ) : null}

      {edit === "news" ? (
        <NewsSheet
          post={news[0] ?? null}
          media={media}
          onSave={onSaveNews}
          onToast={onToast}
          onClose={() => setEdit(null)}
        />
      ) : null}

      {edit === "archive" ? (
        <EditSheet title="Это меняет блок “Пометки” в карточке клиента" preview={<NotesMini bullets={bullets} />} onClose={() => setEdit(null)}>
          <div className={css.bullets}>
            {(bullets.length ? bullets : [""]).map((b, i) => (
              <div key={i} className={css.bulletRow}>
                <span className={css.bulletDot} aria-hidden="true" />
                <input className={css.bulletInput} value={b} maxLength={200}
                  placeholder="Короткая отметка из закрытого дела"
                  onChange={(e) => setBullets((arr) => normaliseBullets(arr).map((x, j) => (j === i ? e.target.value : x)))} />
                <button type="button" className={css.iconBtn} aria-label="Удалить строку"
                  onClick={() => setBullets((arr) => normaliseBullets(arr).filter((_, j) => j !== i))}>
                  <ATrash />
                </button>
              </div>
            ))}
            <button type="button" className={css.addBullet} onClick={() => setBullets((arr) => [...normaliseBullets(arr), ""])}>
              <APlus /> Добавить отметку
            </button>
          </div>
        </EditSheet>
      ) : null}

      {edit === "temple" ? (
        <EditSheet title="Это меняет блок “Фан-блок и спецверсия” в карточке клиента" preview={<FanMini profile={f} />} onClose={() => setEdit(null)}>
          <label className={css.field}>
            <span className={css.fieldLbl}>Название фан-блока</span>
            <input className={css.input} value={f.temple_title} maxLength={120}
              placeholder="Закрытый архив персонажа"
              onChange={(e) => set("temple_title", e.target.value)} />
          </label>
          <label className={css.field}>
            <span className={css.fieldLbl}>Описание фан-доступа</span>
            <textarea className={css.textArea} value={f.temple_blurb} maxLength={2000} rows={6}
              placeholder="Что откроется подписчикам: backstage, личные отметки, закрытые медиа."
              onChange={(e) => set("temple_blurb", e.target.value)} />
          </label>
          <label className={css.field}>
            <span className={css.fieldLbl}>Спец-текст</span>
            <input className={css.input} value={f.special_text} maxLength={300}
              placeholder="Текст спецверсии, когда она откроется"
              onChange={(e) => set("special_text", e.target.value)} />
          </label>
          <label className={css.field}>
            <span className={css.fieldLbl}>Когда открыть спецверсию, UTC+5</span>
            <input className={css.input} value={f.special_opens_at} maxLength={40}
              placeholder="Например: 2026-07-01 20:00"
              onChange={(e) => set("special_opens_at", e.target.value)} />
          </label>
          <label className={css.field}>
            <span className={css.fieldLbl}>Квест для записи на спецверсию</span>
            <select className={css.input} value={f.special_quest_id}
              onChange={(e) => set("special_quest_id", e.target.value)}>
              <option value="">— не привязано —</option>
              {quests.filter((q) => q.active).map((q) => (
                <option key={q.id} value={q.id}>{q.name || q.short_name || q.id}</option>
              ))}
            </select>
            <span className={css.fieldHint}>На него бронируется спецверсия — берём только расписание. Клиенту не показываем ни название квеста, ни адрес.</span>
          </label>
          <div className={css.toggleRow}>
            <AEye />
            <span className={css.toggleLbl}>
              Публичная карточка
              <span className={css.toggleSub}>После одобрения клиенты увидят это досье.</span>
            </span>
            <button type="button" className={cn(css.switch, f.is_public && css.on)} role="switch" aria-checked={f.is_public}
              aria-label="Публичная карточка" onClick={() => set("is_public", !f.is_public)} />
          </div>
        </EditSheet>
      ) : null}
    </div>
  );
}

function ClientActorPreview({
  profile,
  bullets,
  portrait,
  portraitUrl,
  media,
  news,
  joined,
}: {
  profile: ActorProfileFields;
  bullets: string[];
  portrait?: MediaAsset;
  portraitUrl: string;
  media: MediaAsset[];
  news: NewsPost[];
  joined: boolean;
}) {
  return (
    <DossierCanvas
      mode="preview"
      joined={joined}
      profile={profile}
      bullets={bullets}
      portrait={portrait}
      portraitUrl={portraitUrl}
      media={media}
      news={news}
      completion={profileCompletion(profile, bullets, portraitUrl, media, news)}
      onEdit={() => undefined}
      onMediaEdit={() => undefined}
    />
  );
}

function DossierCanvas({
  mode,
  profile,
  bullets,
  portrait,
  portraitUrl,
  media,
  news,
  completion,
  onEdit,
  onMediaEdit,
  onRecordCircle,
  joined,
}: {
  mode: Mode;
  joined: boolean;
  profile: ActorProfileFields;
  bullets: string[];
  portrait?: MediaAsset;
  portraitUrl: string;
  media: MediaAsset[];
  news: NewsPost[];
  completion: ReturnType<typeof profileCompletion>;
  onEdit: (target: EditTarget) => void;
  onMediaEdit: (asset?: MediaAsset) => void;
  onRecordCircle?: () => void;
}) {
  const editable = mode === "editor";
  const [descOpen, setDescOpen] = useState(false);
  const [legendOverflows, setLegendOverflows] = useState(false);
  const legendRef = useRef<HTMLParagraphElement>(null);
  const heroStyle = portrait ? cropPreviewStyle(portrait.crops.dossier_hero ?? defaultCrop(DOSSIER_ASPECT), filterToCss(portrait.filter)) : undefined;
  const tiles = normalisePreviewMedia(media.length ? media : portraitUrl ? [fallbackPortraitMedia(portraitUrl)] : []).slice(0, 6);
  const circleItems = media.filter((m) => m.fanOnly && (m.kind === "video" || m.kind === "photo")).slice(0, 4);
  const notes = bullets.filter((b) => b.trim());
  const displayName = profile.character_name.trim() || "Имя персонажа";
  const legend = profile.atmos_status.trim() || "Добавь легенду персонажа: клиенты должны почувствовать живой голос, манеру и атмосферу встречи.";
  const fanTitle = profile.temple_title.trim() || "Закрытый архив персонажа";
  const fanBlurb = profile.temple_blurb.trim() || "Подписка открывает личные отметки, корпоративный микроблог, закрытые ивенты и медиа.";
  const specialText = profile.special_text.trim();
  const newsFeed = buildPreviewNews(news, notes, profile.atmos_status);

  useLayoutEffect(() => {
    const el = legendRef.current;
    if (!el) return;
    setLegendOverflows(el.scrollHeight > el.clientHeight + 2);
  }, [legend, descOpen, joined]);

  return (
    <section className={cn(css.dossierCanvas, editable && css.isEditing, joined && css.isFanMember)}>
      <div className={css.dossierTopbar}>
        <span className={css.dossierBack}>Личное дело</span>
        <span className={css.dossierStamp}>{joined ? "вид подписчика" : "редактируется"}</span>
      </div>

      <div className={cn(css.dossierHero, css.dossierFrame)}>
        <button type="button" data-edit-label={joined ? "→ фан-аватар" : "→ портрет"} className={cn(css.dossierPhoto, editable && css.editHotspot, joined && css.isFanPhoto)} onClick={() => editable && onEdit("portrait")}>
          {portraitUrl ? (
            <img src={portraitUrl} alt="" style={heroStyle} />
          ) : (
            <span className={css.dossierEmptyPortrait}><AProfile /> портрет</span>
          )}
        </button>

        <div className={css.dossierIntro}>
          <button type="button" data-edit-label="→ имя" className={cn(css.dossierIdentity, editable && css.editHotspot)} onClick={() => editable && onEdit("identity")}>
            <FitH1>{displayName}</FitH1>
          </button>
          <button
            type="button"
            data-edit-label="→ описание"
            className={cn(css.dossierBlog, editable && css.editHotspot)}
            onClick={() => editable ? onEdit("blog") : setDescOpen((o) => !o)}
          >
            <p ref={legendRef} className={descOpen ? css.isOpen : ""}>{legend}</p>
            {(legendOverflows || descOpen) ? <span className={css.dossierMore}>{descOpen ? "свернуть ▲" : "подробнее ▼"}</span> : null}
          </button>
        </div>
      </div>

      {editable && joined ? (
        <section className={cn(css.dossierPanel, css.dossierFrame, css.dossierStorySection)} data-edit-label="→ кружки">
          <div className={css.dossierPanelHead}><span>Кружки персонажа</span><em>тап — записать stories</em></div>
          <div className={css.dossierStoryRow} aria-label="Кружки персонажа">
            {Array.from({ length: 4 }).map((_, i) => {
              const c = circleItems[i];
              const src = portraitSrc(c);
              return (
                <button
                  key={c?.id ?? `circle-${i}`}
                  type="button"
                  className={cn(css.dossierStoryCircle, !c && css.emptyTile)}
                  onClick={(e) => { e.stopPropagation(); if (c) onMediaEdit(c); else onRecordCircle?.(); }}
                >
                  {c && src ? (
                    <img src={src} alt="" style={cropPreviewStyle(c.crops.media_tile, filterToCss(c.filter))} />
                  ) : (
                    <span className={css.mediaEmptyMark}><APlus /></span>
                  )}
                  <span className={css.dossierStoryLabel}>{c ? "видео" : "записать"}</span>
                </button>
              );
            })}
          </div>
        </section>
      ) : null}

      <div className={css.dossierColumns}>
        <div className={css.dossierColumn}>
          <button type="button" data-edit-label="→ фан-блок" className={cn(css.dossierPanel, css.dossierFan, css.dossierFrame, editable && css.editHotspot, joined && css.isJoined)} onClick={() => editable && onEdit("temple")}>
            {joined ? (
              <span className={cn(css.dossierButton, css.dossierFanButton)}>Вы подписаны · отписаться</span>
            ) : (
              <>
                <div className={css.dossierPanelHead}><span>{fanTitle}</span><em>фансервис</em></div>
                <p>{fanBlurb}</p>
                <div className={css.dossierFanPerks}>
                  <span>личные отметки</span>
                  <span>корпоративный микроблог</span>
                  <span>закрытые ивенты</span>
                  <span>медиа</span>
                </div>
                <span className={cn(css.dossierButton, css.dossierFanButton)}>Подписаться · 0,40 UTC/мес</span>
              </>
            )}
          </button>

          <section className={cn(css.dossierPanel, css.dossierFrame, css.dossierMediaSection, editable && css.editHotspot)} data-edit-label="→ медиа" onClick={() => editable && onMediaEdit()}>
            <div className={css.dossierPanelHead}><span>Медиа-архив</span><em>{tiles.length || 0} файлов</em></div>
            <div className={css.dossierMediaRow} aria-label="Медиа актера">
              {(tiles.length ? tiles : [undefined, undefined, undefined]).map((asset, i) => {
                const locked = Boolean(asset?.fanOnly) && !joined;
                const src = portraitSrc(asset);
                const isVideo = asset?.kind === "video";
                return (
                  <button
                    key={asset?.id ?? `slot-${i}`}
                    type="button"
                    className={cn(css.dossierMediaTile, asset?.fanOnly && css.fanOnly, locked && css.isLocked, !asset && css.emptyTile)}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (editable) onMediaEdit(asset);
                    }}
                  >
                    {asset && src ? (
                      <img src={src} alt="" style={cropPreviewStyle(asset.crops.media_tile, filterToCss(asset.filter))} />
                    ) : (
                      <span className={css.mediaEmptyMark}><APlus /></span>
                    )}
                    <span className={css.dossierMediaShade} aria-hidden="true" />
                    {locked || isVideo ? <span className={css.dossierMediaIcon}>{locked ? <AFan /> : <APlay />}</span> : null}
                    <span className={css.dossierMediaLabel}>{locked ? "Фан-допуск" : asset ? mediaLabel(asset, i) : "Добавить"}</span>
                    <span className={css.dossierMediaTime}>{locked ? "0,40 UTC" : isVideo ? "видео" : asset ? "архив" : "медиа"}</span>
                  </button>
                );
              })}
            </div>
          </section>
        </div>

        <div className={css.dossierColumn}>
          <section className={cn(css.dossierPanel, css.dossierFrame)}>
            <div className={css.dossierPanelHead}><span>Встретил на квестах</span><em>авто</em></div>
            <span className={css.dossierGatedMark}>формируется автоматически из назначений</span>
          </section>

          {(specialText || profile.special_opens_at || editable) ? (
            <button type="button" data-edit-label="→ спецверсия" className={cn(css.dossierPanel, css.dossierSpecial, css.dossierFrame, editable && css.editHotspot, joined && css.isRevealed)} onClick={() => editable && onEdit("temple")}>
              <div>
                <div className={css.dossierPanelHead}><span>Моя спецверсия</span><em>{specialText ? "заполнено" : "пусто"}</em></div>
                <h2>{trim(specialText || "Спецверсия не заполнена", 74)}</h2>
                <p>{specialText ? (profile.special_opens_at ? `Открывается ${profile.special_opens_at}` : "Спецверсия персонажа") : "Тапни, чтобы добавить название и описание спецверсии"}</p>
              </div>
              <span className={css.dossierSpecialButton}>Записаться на спецверсию</span>
            </button>
          ) : null}
        </div>
      </div>

      <div className={css.dossierGrid}>
        <button type="button" data-edit-label="→ новости" className={cn(css.dossierPanel, css.dossierNews, css.dossierFrame, editable && css.editHotspot, joined && css.isRevealed)} onClick={() => editable && onEdit("news")}>
          <div className={css.dossierPanelHead}>
            <span>Новости</span>
            <em>{news.length ? `${news.length} записей` : "создать"}</em>
          </div>
          <span className={css.dossierGatedMark}>{joined ? "видно подписчику" : "у клиента видно только после подписки"}</span>
          <div className={css.dossierNewsList}>
            {newsFeed.slice(0, 3).map((item, index) => (
              <article key={`${item.title}-${index}`} className={css.dossierNewsItem}>
                <h3>{item.title}</h3>
                <p>{trim(item.text, 150)}</p>
              </article>
            ))}
          </div>
        </button>

        <button type="button" data-edit-label="→ пометки" className={cn(css.dossierPanel, css.dossierArchive, css.dossierFrame, editable && css.editHotspot, joined && css.isRevealed)} onClick={() => editable && onEdit("archive")}>
          <div className={css.dossierPanelHead}><span>Пометки на полях</span><em>{notes.length || 0} записей</em></div>
          <span className={css.dossierGatedMark}>{joined ? "видно подписчику" : "у клиента видно только после подписки"}</span>
          <ul className={css.dossierNotesList}>
            {(notes.length ? notes : ["Добавь архивные отметки персонажа."]).slice(0, 5).map((note, index) => (
              <li key={`${note}-${index}`}>{note}</li>
            ))}
          </ul>
        </button>
      </div>

      {editable ? (
        <div className={css.dossierEditNote}>
          <span>{completion.next}</span>
          <em>метки на блоках показывают, что именно редактируется</em>
        </div>
      ) : null}
    </section>
  );
}

function PortraitSheet({
  photos,
  portrait,
  portraitUrl,
  busy,
  onUpload,
  onPick,
  onCrop,
  onFilter,
  onSetFanPortrait,
  onUploadFan,
  busyFan,
  onClose,
}: {
  photos: MediaAsset[];
  portrait?: MediaAsset;
  portraitUrl: string;
  busy: boolean;
  onUpload: () => void;
  onPick: (asset: MediaAsset) => void;
  onCrop: (rect: CropRect) => void;
  onFilter: (preset: string) => void;
  onSetFanPortrait: (mediaId: number, fanPortrait: boolean) => Promise<{ ok: boolean; error?: string }>;
  onUploadFan: () => void;
  busyFan: boolean;
  onClose: () => void;
}) {
  return (
    <EditSheet title="Это меняет блок “Портрет” в карточке клиента" preview={<PortraitMini src={portraitUrl} asset={portrait} />} onClose={onClose}>
      <button type="button" className={cn(css.uploadZone, busy && css.busy)} onClick={onUpload} disabled={busy}>
        <span className={css.uploadIco}><AUpload /></span>
        <span className={css.uploadText}>
          <span className={css.uploadTitle}>{busy ? "Загрузка…" : "Загрузить новый портрет"}</span>
          <span className={css.uploadSub}>Фото уйдёт через POST /media, затем здесь же настраивается кроп.</span>
        </span>
      </button>

      {photos.length ? (
        <div className={css.pickGrid}>
          {photos.map((m) => (
            <button key={m.id} type="button" className={cn(css.pickItem, portrait?.id === m.id && css.on)} onClick={() => onPick(m)}>
              <img src={portraitSrc(m)} alt={m.label} />
            </button>
          ))}
        </div>
      ) : (
        <div className={css.empty}>В медиатеке пока нет фото.</div>
      )}

      {portrait && portraitUrl ? (
        <div className={css.sheetEditorBlock}>
          <div className={css.builderCardHead}><span><ACrop /> Кроп hero-портрета</span><em>dossier_hero · 3:4</em></div>
          <CropEditor src={portraitUrl} aspect={DOSSIER_ASPECT} value={portrait.crops.dossier_hero ?? defaultCrop(DOSSIER_ASPECT)} onChange={onCrop} />
          <div className={css.cropHint}>Именно этот кадр повторится в клиентском досье.</div>
          <div className={css.filterRow}>
            {FILTER_PRESETS.map((p) => (
              <button key={p.id} type="button" className={cn(css.filterChip, currentPreset(portrait.filter) === p.id && css.on)} onClick={() => onFilter(p.id)}>
                <img className={css.filterSwatch} style={{ filter: p.css === "none" ? undefined : p.css }} src={portraitUrl} alt="" />
                <span className={css.filterName}>{p.name}</span>
              </button>
            ))}
          </div>
        </div>
      ) : null}

      <div className={css.sheetEditorBlock}>
        <div className={css.builderCardHead}><span><AFan /> Фан-аватар</span><em>виден подписчикам</em></div>
        <div className={css.cropHint}>Отдельное лицо для подписчиков фан-клуба — заменяет портрет в режиме «как подписчик».</div>
        <button type="button" className={cn(css.uploadZone, busyFan && css.busy)} onClick={onUploadFan} disabled={busyFan}>
          <span className={css.uploadIco}><AUpload /></span>
          <span className={css.uploadText}>
            <span className={css.uploadTitle}>{busyFan ? "Загрузка…" : "Загрузить фан-аватар"}</span>
            <span className={css.uploadSub}>Или выбери фото ниже — оно станет фан-аватаром.</span>
          </span>
        </button>
        {photos.length ? (
          <div className={css.pickGrid}>
            {photos.map((m) => (
              <button key={m.id} type="button" className={cn(css.pickItem, m.fanPortrait && css.on)} onClick={() => onSetFanPortrait(m.id, !m.fanPortrait)}>
                <img src={portraitSrc(m)} alt={m.label} />
              </button>
            ))}
          </div>
        ) : null}
      </div>
    </EditSheet>
  );
}

function MediaSheet({
  media,
  selected,
  busy,
  onSelect,
  onUpload,
  onCrop,
  onFilter,
  onToggleFan,
  onDelete,
  onClose,
}: {
  media: MediaAsset[];
  selected?: MediaAsset;
  busy: boolean;
  onSelect: (asset: MediaAsset) => void;
  onUpload: () => void;
  onCrop: (asset: MediaAsset, rect: CropRect) => void;
  onFilter: (asset: MediaAsset, preset: string) => void;
  onToggleFan: (asset: MediaAsset) => void;
  onDelete: (asset: MediaAsset) => void;
  onClose: () => void;
}) {
  const [delArm, setDelArm] = useState(false);
  useEffect(() => { setDelArm(false); }, [selected?.id]);
  const src = portraitSrc(selected);
  return (
    <EditSheet title="Это меняет блок “Медиа” в карточке клиента" preview={<MediaMini media={media} selected={selected} />} onClose={onClose}>
      <button type="button" className={cn(css.uploadZone, busy && css.busy)} onClick={onUpload} disabled={busy}>
        <span className={css.uploadIco}><AUpload /></span>
        <span className={css.uploadText}>
          <span className={css.uploadTitle}>{busy ? "Загрузка…" : "Добавить медиа"}</span>
          <span className={css.uploadSub}>Фото или видео появится в плитках этой страницы.</span>
        </span>
      </button>

      {media.length ? (
        <div className={css.pickGrid}>
          {media.map((m) => (
            <button key={m.id} type="button" className={cn(css.pickItem, selected?.id === m.id && css.on)} onClick={() => onSelect(m)}>
              {portraitSrc(m) ? <img src={portraitSrc(m)} alt={m.label} /> : <span className={css.mediaEmptyMark}>{m.kind}</span>}
            </button>
          ))}
        </div>
      ) : (
        <div className={css.empty}>Медиа пока нет. Добавь первый файл.</div>
      )}

      {selected && src ? (
        <div className={css.sheetEditorBlock}>
          <div className={css.builderCardHead}>
            <span><AMedia /> {mediaLabel(selected, 0)}</span>
            <div className={css.mediaActions}>
              <button type="button" className={cn(css.fanChip, delArm && css.on)} onClick={() => { if (delArm) { onDelete(selected); setDelArm(false); } else { setDelArm(true); } }}>
                {delArm ? "точно?" : "удалить"}
              </button>
            </div>
          </div>
          <div className={css.toggleRow}>
            <AFan />
            <span className={css.toggleLbl}>
              Только для подписчиков
              <span className={css.toggleSub}>{selected.fanOnly ? "Это медиа видят только фанаты — без подписки закрыто замком." : "Видно всем. Включи, чтобы спрятать под фан-клуб."}</span>
            </span>
            <button type="button" className={cn(css.switch, selected.fanOnly && css.on)} role="switch" aria-checked={selected.fanOnly} aria-label="Только для подписчиков" onClick={() => onToggleFan(selected)} />
          </div>
          <CropEditor src={src} aspect={TILE_ASPECT} value={selected.crops.media_tile ?? defaultCrop(TILE_ASPECT)} onChange={(rect) => onCrop(selected, rect)} />
          <div className={css.cropHint}>Квадратная плитка медиа в клиентском досье.</div>
          <div className={css.filterRow}>
            {FILTER_PRESETS.map((p) => (
              <button key={p.id} type="button" className={cn(css.filterChip, currentPreset(selected.filter) === p.id && css.on)} onClick={() => onFilter(selected, p.id)}>
                <img className={css.filterSwatch} style={{ filter: p.css === "none" ? undefined : p.css }} src={src} alt="" />
                <span className={css.filterName}>{p.name}</span>
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </EditSheet>
  );
}

function NewsSheet({
  post,
  media,
  onSave,
  onToast,
  onClose,
}: {
  post: NewsPost | null;
  media: MediaAsset[];
  onSave: (input: NewsInput, id?: number) => Promise<NewsResult>;
  onToast: (msg: string, isErr?: boolean) => void;
  onClose: () => void;
}) {
  const [title, setTitle] = useState(post?.title ?? "");
  const [body, setBody] = useState(post?.body ?? "");
  const [coverId, setCoverId] = useState<number | null>(post?.coverMediaId ?? null);
  const [busy, setBusy] = useState(false);
  const photos = media.filter((m) => m.kind === "photo");

  async function commit(submit: boolean) {
    if (busy) return;
    setBusy(true);
    const res = await onSave({ title, body, coverMediaId: coverId, submit }, post?.id);
    setBusy(false);
    if (res.ok) {
      onToast(submit ? "Новость отправлена на проверку" : "Черновик новости сохранён");
      onClose();
    } else {
      onToast(res.error ?? "Не удалось сохранить новость", true);
    }
  }

  return (
    <EditSheet title="Это меняет блок “Новости” в карточке клиента" preview={<NewsMini post={post} />} onClose={onClose}>
      <label className={css.field}>
        <span className={css.fieldLbl}>Заголовок</span>
        <input className={css.input} value={title} maxLength={160} placeholder="Коротко, как в сторис архива"
          onChange={(e) => setTitle(e.target.value)} />
      </label>
      <label className={css.field}>
        <span className={css.fieldLbl}>Текст</span>
        <textarea className={css.textArea} value={body} maxLength={4000} rows={7}
          placeholder="Что появится справа от фан-клуба в личном деле"
          onChange={(e) => setBody(e.target.value)} />
      </label>
      <span className={css.fieldLbl}>Обложка</span>
      {photos.length ? (
        <div className={css.pickGrid}>
          {photos.slice(0, 9).map((m) => (
            <button key={m.id} type="button" className={cn(css.pickItem, coverId === m.id && css.on)} onClick={() => setCoverId(m.id)}>
              <img src={portraitSrc(m)} alt={m.label} />
            </button>
          ))}
        </div>
      ) : (
        <div className={css.empty}>Для обложки сначала добавь фото в медиа.</div>
      )}
      <div className={css.sheetActions}>
        <button type="button" className={cn(css.btn, css.primary)} disabled={busy} onClick={() => commit(true)}>
          {busy ? "…" : "На проверку"}
        </button>
        <button type="button" className={css.btn} disabled={busy} onClick={() => commit(false)}>
          Черновик
        </button>
      </div>
    </EditSheet>
  );
}

// #2: заголовок-имя всегда в одну строку — уменьшаем кегль, пока влезает.
function FitH1({ children, className }: { children: string; className?: string }) {
  const ref = useRef<HTMLHeadingElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.fontSize = "";
    let size = parseFloat(window.getComputedStyle(el).fontSize) || 24;
    let guard = 0;
    while (el.scrollWidth > el.clientWidth && size > 12 && guard++ < 48) {
      size -= 1;
      el.style.fontSize = `${size}px`;
    }
  }, [children]);
  return <h1 ref={ref} className={className} style={{ whiteSpace: "nowrap", overflow: "hidden" }}>{children}</h1>;
}

function EditSheet({ title, preview, onClose, children }: { title: string; preview?: ReactNode; onClose: () => void; children: ReactNode }) {
  return (
    <>
      <div className={css.backdrop} onClick={onClose} />
      <div className={css.sheet} role="dialog" aria-modal="true">
        <div className={css.sheetHandle} />
        <div className={css.sheetHead}>
          <span className={css.sheetTitle}>{title}</span>
          <button type="button" className={css.sheetClose} aria-label="Закрыть" onClick={onClose}><RClose /></button>
        </div>
        <div className={css.sheetBody}>
          {preview ? <div className={css.sheetMiniPreview}>{preview}</div> : null}
          {children}
        </div>
        {/* #3: явная галочка-подтверждение, что правки приняты (редактор автосейвит). */}
        <div className={css.sheetFoot}>
          <button type="button" className={css.sheetDone} onClick={onClose}>
            <span aria-hidden="true">✓</span> Готово
          </button>
        </div>
      </div>
    </>
  );
}

function IdentityMini({ profile }: { profile: ActorProfileFields }) {
  return (
    <div className={css.miniBlock}>
      <span>Имя в hero</span>
      <strong>{profile.character_name.trim() || "Имя персонажа"}</strong>
    </div>
  );
}

function LegendMini({ text }: { text: string }) {
  return (
    <div className={css.miniBlock}>
      <span>Легенда в шапке</span>
      <p>{trim(text || "Описание пока не заполнено.", 180)}</p>
    </div>
  );
}

function PortraitMini({ src, asset }: { src: string; asset?: MediaAsset }) {
  const style = asset ? cropPreviewStyle(asset.crops.dossier_hero ?? defaultCrop(DOSSIER_ASPECT), filterToCss(asset.filter)) : undefined;
  return (
    <div className={css.miniPortrait}>
      {src ? <img src={src} alt="" style={style} /> : <span><AProfile /></span>}
      <em>dossier_hero · 3:4</em>
    </div>
  );
}

function MediaMini({ media, selected }: { media: MediaAsset[]; selected?: MediaAsset }) {
  const items = (selected ? [selected, ...media.filter((m) => m.id !== selected.id)] : media).slice(0, 4);
  return (
    <div className={css.miniMediaRow}>
      {(items.length ? items : [undefined, undefined, undefined]).map((item, index) => {
        const src = portraitSrc(item);
        return (
          <span key={item?.id ?? `mini-${index}`} className={css.miniMediaTile}>
            {item && src ? <img src={src} alt="" style={cropPreviewStyle(item.crops.media_tile, filterToCss(item.filter))} /> : <APlus />}
            {item?.kind === "video" ? <i><APlay /></i> : null}
          </span>
        );
      })}
    </div>
  );
}

function FanMini({ profile }: { profile: ActorProfileFields }) {
  return (
    <div className={css.miniBlock}>
      <span>{profile.temple_title || "Закрытый архив персонажа"}</span>
      <p>{trim(profile.temple_blurb || "Фансервис, личные отметки, закрытые медиа.", 160)}</p>
      <strong>Подписаться · 0,40 UTC/мес</strong>
    </div>
  );
}

function NewsMini({ post }: { post: NewsPost | null }) {
  return (
    <div className={css.miniBlock}>
      <span>Новости</span>
      <strong>{post?.title || "Новая запись"}</strong>
      <p>{trim(post?.body || "У клиента видно только после подписки.", 150)}</p>
    </div>
  );
}

function NotesMini({ bullets }: { bullets: string[] }) {
  const notes = bullets.filter((b) => b.trim()).slice(0, 3);
  return (
    <div className={css.miniBlock}>
      <span>Пометки на полях</span>
      {(notes.length ? notes : ["Пока нет пометок."]).map((note, index) => <p key={`${note}-${index}`}>• {note}</p>)}
    </div>
  );
}

function resolvePortrait(photoUrl: string, media: MediaAsset[]): MediaAsset | undefined {
  const target = (photoUrl || "").split("?")[0];
  if (!target) return undefined;
  // Сравниваем по пути без ?токена: подписанные media-URL пересоздаются на каждой загрузке,
  // поэтому точное сравнение со хранимым photo_url «теряло» портрет после перезахода.
  return media.filter((m) => m.kind === "photo").find((m) =>
    [m.streamUrl, m.posterUrl].some((u) => (u || "").split("?")[0] === target));
}

function fallbackPortraitMedia(src: string): MediaAsset {
  return {
    id: -1,
    kind: "photo",
    label: "Портрет",
    fileKey: "",
    thumbKey: "",
    width: 0,
    height: 0,
    fanOnly: false,
    fanPortrait: false,
    filter: {},
    crops: {},
    streamUrl: src,
    posterUrl: src,
    createdAt: 0,
  };
}

function portraitSrc(asset?: MediaAsset): string {
  return asset?.posterUrl || asset?.streamUrl || "";
}

function mediaLabel(asset: MediaAsset, index: number): string {
  const filterLabel = typeof asset.filter.__label === "string" ? asset.filter.__label : "";
  return filterLabel || asset.label || `${asset.kind === "video" ? "Видео" : "Фото"} #${String(index + 1).padStart(2, "0")}`;
}

function normalisePreviewMedia(items: MediaAsset[]): MediaAsset[] {
  if (items.length >= 4) return items.slice(0, 4);
  return items;
}

function buildPreviewNews(news: NewsPost[], archive: string[], atmosStatus: string): { title: string; text: string }[] {
  const publishedLike = news.length
    ? news.map((item) => ({
      title: item.title || "Запись архива",
      text: item.body || "Новость пока без текста.",
    }))
    : [];
  const noteItems = archive.map((text, index) => ({
    title: index === 0 ? "Архивная отметка" : `Отметка ${String(index + 1).padStart(2, "0")}`,
    text,
  }));
  const fallback = [{
    title: "Личный архив",
    text: atmosStatus || "Личный архив пополняется после каждой смены.",
  }];
  return [...publishedLike, ...noteItems, ...fallback].slice(0, 5);
}

function profileCompletion(profile: ActorProfileFields, bullets: string[], portraitUrl: string, media: MediaAsset[], news: NewsPost[]) {
  const checks = [
    !!portraitUrl,
    profile.character_name.trim().length > 1,
    profile.display_role.trim().length > 2,
    profile.character_status.trim().length > 2,
    profile.atmos_status.trim().length > 80,
    media.length >= 3,
    news.length > 0 || bullets.filter((b) => b.trim()).length >= 2,
    profile.temple_title.trim().length > 2 || profile.temple_blurb.trim().length > 20,
  ];
  const done = checks.filter(Boolean).length;
  const percent = Math.round((done / checks.length) * 100);
  const next = !checks[0] ? "выбери портрет"
    : !checks[4] ? "добавь блог персонажа"
      : !checks[5] ? "добавь медиа в плитки"
        : !checks[6] ? "создай новость или архивные отметки"
          : !checks[7] ? "оформи фан-блок"
            : "досье выглядит живым";
  return { percent, next };
}

function trim(value: string | undefined, max: number): string {
  const text = (value ?? "").trim();
  return text.length > max ? `${text.slice(0, max - 1).trim()}…` : text;
}

function normaliseBullets(items: string[]): string[] {
  return items.length ? items : [""];
}

/** Детерминированный снимок полей анкеты для сравнения «менялось ли» (archive_notes из bullets). */
function serializeFields(f: ActorProfileFields, bullets: string[]): string {
  return JSON.stringify({ ...f, archive_notes: JSON.stringify(bullets.filter((b) => b.trim())) });
}

function cn(...parts: Array<string | undefined | null | false>): string {
  return parts.filter(Boolean).join(" ");
}

function readDims(file: File): Promise<{ w: number; h: number } | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const done = (w: number, h: number) => { URL.revokeObjectURL(url); resolve(w && h ? { w, h } : null); };
    const img = new Image();
    img.onload = () => done(img.naturalWidth, img.naturalHeight);
    img.onerror = () => done(0, 0);
    img.src = url;
  });
}
